<template>
  <div v-if="visible" class="symbol-search-overlay" @click.self="close">
    <div class="symbol-search-modal" :class="{ dark: isDark }">
      <!-- Header -->
      <div class="symbol-search-header">
        <h2 class="symbol-search-title">Symbol search</h2>
        <button class="symbol-search-close" @click="close">&times;</button>
      </div>

      <!-- Search Input -->
      <div class="symbol-search-input-wrapper">
        <input
          ref="searchInput"
          v-model="query"
          type="text"
          class="symbol-search-input"
          placeholder="Search symbols..."
          @input="onQueryChange"
          @keydown.enter="selectFirst"
          @keydown.esc="close"
        />
        <button v-if="query" class="symbol-search-clear" @click="clearQuery">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </button>
      </div>

      <!-- Segment Filter Tabs -->
      <div class="symbol-search-filters">
        <button
          v-for="seg in visibleSegments"
          :key="seg.key"
          :class="['segment-btn', { active: activeSegment === seg.key }]"
          @click="setSegment(seg.key)"
        >{{ seg.label }}</button>
      </div>

      <!-- Results -->
      <div class="symbol-search-results" ref="resultsList">
        <div v-if="loading" class="symbol-search-loading">
          <span class="spinner"></span> Searching...
        </div>
        <div v-else-if="filteredResults.length === 0 && query.length > 0" class="symbol-search-empty">
          No symbols found
        </div>
        <div
          v-for="(item, idx) in filteredResults"
          :key="idx"
          class="symbol-search-item"
          @click="selectItem(item)"
        >
          <div class="search-item-left">
            <span class="search-item-symbol">{{ getDisplaySymbol(item) }}</span>
            <span class="search-item-name">{{ getDisplayName(item) }}</span>
          </div>
          <div class="search-item-right">
            <span class="search-item-segment">{{ getSegmentLabel(item) }}</span>
            <!-- Favourite / watchlist toggle -->
            <button
              class="search-item-fav-btn"
              :class="{ 'is-fav': isInWatchlist(item) }"
              :title="isInWatchlist(item) ? 'Remove from watchlist' : 'Add to watchlist'"
              @click.stop="toggleWatchlist(item)"
            >
              <svg
                width="15" height="15"
                viewBox="0 0 24 24"
                :fill="isInWatchlist(item) ? 'currentColor' : 'none'"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import fyersApi from '../services/fyers_service';
import upstoxApi from '../services/upstox_service';
import AliceBlueService from '../services/aliceblue_service';
import IndianMarketService from '../services/indian_market_service';
import watchlistStore from '../services/watchlistStore';

export default {
  name: 'SymbolSearch',
  props: {
    isDark: { type: Boolean, default: false },
    allSymbols: { type: Object, default: () => ({}) },
    resultFilter: { type: Function, default: null },
    segmentsOverride: { type: Array, default: null },
    displaySymbolFormatter: { type: Function, default: null },
    displayNameFormatter: { type: Function, default: null }
  },
  data() {
    return {
      visible: false,
      query: '',
      activeSegment: 'all',
      loading: false,
      results: [],
      searchTimer: null,
      // Reactive snapshot of watchlist keys so the star buttons update instantly
      watchlistKeys: new Set(),
      segments: [
        { key: 'all', label: 'All' },
        { key: 'stocks', label: 'Stocks' },
        { key: 'futures', label: 'Futures' },
        { key: 'options', label: 'Options' },
        { key: 'indices', label: 'Indices' },
        { key: 'commodity', label: 'Commodity' },
        { key: 'forex', label: 'Forex' },
        { key: 'crypto_spot', label: 'Crypto Spot' },
        { key: 'crypto_futures', label: 'Crypto Futures' }
      ]
    }
  },
  computed: {
    visibleSegments() {
      return Array.isArray(this.segmentsOverride) && this.segmentsOverride.length
        ? this.segmentsOverride
        : this.segments;
    },
    filteredResults() {
      const results = this.applyResultFilter(this.results);
      if (this.activeSegment === 'all') return results;
      return results.filter(item => this.getItemSegmentKey(item) === this.activeSegment);
    }
  },
  methods: {
    show(prefill) {
      this.visible = true;
      this.query = prefill || '';
      this.activeSegment = this.defaultSegmentKey();
      this.results = [];
      this.refreshWatchlistKeys();
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
          this.$refs.searchInput.select();
        }
        if (this.query) {
          this.performSearch();
        }
      });
    },
    close() {
      this.visible = false;
      this.query = '';
      this.results = [];
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
    },
    clearQuery() {
      this.query = '';
      this.results = [];
      this.$refs.searchInput.focus();
    },
    setSegment(key) {
      const allowed = this.visibleSegments.some(seg => seg.key === key);
      this.activeSegment = allowed ? key : this.defaultSegmentKey();
    },
    defaultSegmentKey() {
      const hasAll = this.visibleSegments.find(seg => seg.key === 'all');
      return (hasAll || this.visibleSegments[0] || { key: 'all' }).key;
    },
    applyResultFilter(results) {
      if (typeof this.resultFilter !== 'function') return results;
      return results.filter(item => {
        try {
          return this.resultFilter(item);
        } catch (error) {
          console.warn('[SymbolSearch] result filter failed:', error);
          return false;
        }
      });
    },
    onQueryChange() {
      // Capitalize the input
      this.query = this.query.toUpperCase();
      // Debounce the search to avoid excessive API calls
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.performSearch();
      }, 200);
    },
    performSearch() {
      if (!this.query || this.query.length < 1) {
        this.results = [];
        return;
      }

      const uppercaseQuery = this.query.toUpperCase().split('.')[0];

      // First: search local allSymbols (Binance, pre-loaded NSE, etc.)
      const localResults = this.searchLocal(uppercaseQuery);
      this.results = localResults;

      if (this.$store && this.$store.getters.indianMarketDataEnabled && this.query.length >= 1) {
        this.loading = true;
        IndianMarketService.searchSymbols(this.query, 50).then(indianResults => {
          if (indianResults && indianResults.length > 0) {
            indianResults.forEach(item => {
              const key = 'indian-market:' + (item.instrument_key || item.symbol || item.trading_symbol);
              this.$set(this.allSymbols, key, item);
            });

            const mergedResults = this.searchLocal(uppercaseQuery);
            mergedResults.sort((a, b) => {
              const aIsIndian = this.isIndianMarketSymbol(a);
              const bIsIndian = this.isIndianMarketSymbol(b);
              if (aIsIndian && !bIsIndian) return -1;
              if (!aIsIndian && bIsIndian) return 1;
              if (aIsIndian && bIsIndian) {
                const rankDiff = this.getIndianMarketRank(a) - this.getIndianMarketRank(b);
                if (rankDiff !== 0) return rankDiff;
              }
              const aSym = (a.trading_symbol || a.symbol || '').toUpperCase();
              const bSym = (b.trading_symbol || b.symbol || '').toUpperCase();
              const aStarts = aSym.startsWith(uppercaseQuery) ? 0 : 1;
              const bStarts = bSym.startsWith(uppercaseQuery) ? 0 : 1;
              if (aStarts !== bStarts) return aStarts - bStarts;
              return aSym.localeCompare(bSym);
            });

            this.results = mergedResults;
          }
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
        return;
      }

      // Then: if Fyers is authenticated, search Fyers API for all segments
      if (fyersApi.isAuthenticated() && this.query.length >= 1) {
        this.loading = true;
        fyersApi.searchSymbols(this.query, 50, 'ALLMFIPO').then(fyersResults => {
          if (fyersResults && fyersResults.length > 0) {
            // Add Fyers results to parent's allSymbols cache
            fyersResults.forEach(item => {
              const key = 'fyers:' + (item.symbol || item.trading_symbol);
              this.$set(this.allSymbols, key, item);
            });

            // Re-search with merged results
            const mergedResults = this.searchLocal(uppercaseQuery);

            // Prioritize Fyers/Indian market symbols at the top
            mergedResults.sort((a, b) => {
              const aIsFyers = this.isFyersSymbol(a);
              const bIsFyers = this.isFyersSymbol(b);
              if (aIsFyers && !bIsFyers) return -1;
              if (!aIsFyers && bIsFyers) return 1;
              // Within Fyers, sort by prefix match
              const aSym = (a.trading_symbol || a.symbol || '').toUpperCase();
              const bSym = (b.trading_symbol || b.symbol || '').toUpperCase();
              const aStarts = aSym.startsWith(uppercaseQuery) ? 0 : 1;
              const bStarts = bSym.startsWith(uppercaseQuery) ? 0 : 1;
              return aStarts - bStarts;
            });

            this.results = mergedResults;
          }
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
      }
      // If Upstox is authenticated, search Upstox API
      else if (upstoxApi.isAuthenticated() && this.query.length >= 1) {
        this.loading = true;
        upstoxApi.searchSymbols(this.query, 50).then(upstoxResults => {
          if (upstoxResults && upstoxResults.length > 0) {
            // Add Upstox results to parent's allSymbols cache
            upstoxResults.forEach(item => {
              const key = 'upstox:' + (item.instrument_key || item.symbol || item.trading_symbol);
              this.$set(this.allSymbols, key, item);
            });

            // Re-search with merged results
            const mergedResults = this.searchLocal(uppercaseQuery);

            // Prioritize Upstox/Indian market symbols at the top
            mergedResults.sort((a, b) => {
              const aIsUpstox = this.isUpstoxSymbol(a);
              const bIsUpstox = this.isUpstoxSymbol(b);
              if (aIsUpstox && !bIsUpstox) return -1;
              if (!aIsUpstox && bIsUpstox) return 1;
              const aSym = (a.trading_symbol || a.symbol || '').toUpperCase();
              const bSym = (b.trading_symbol || b.symbol || '').toUpperCase();
              const aStarts = aSym.startsWith(uppercaseQuery) ? 0 : 1;
              const bStarts = bSym.startsWith(uppercaseQuery) ? 0 : 1;
              return aStarts - bStarts;
            });

            this.results = mergedResults;
          }
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
      }
      // If AliceBlue is authenticated, search AliceBlue API
      else if (AliceBlueService.isAuthenticated() && this.query.length >= 1) {
        this.loading = true;
        AliceBlueService.searchSymbols(this.query, 50).then(aliceblueResults => {
          if (aliceblueResults && aliceblueResults.length > 0) {
            // Add AliceBlue results to parent's allSymbols cache
            aliceblueResults.forEach(item => {
              const key = 'aliceblue:' + (item.token || item.symbol || item.trading_symbol);
              this.$set(this.allSymbols, key, item);
            });

            // Re-search with merged results
            const mergedResults = this.searchLocal(uppercaseQuery);

            // Prioritize AliceBlue/Indian market symbols at the top
            mergedResults.sort((a, b) => {
              const aIsAliceBlue = this.isAliceBlueSymbol(a);
              const bIsAliceBlue = this.isAliceBlueSymbol(b);
              if (aIsAliceBlue && !bIsAliceBlue) return -1;
              if (!aIsAliceBlue && bIsAliceBlue) return 1;
              const aSym = (a.trading_symbol || a.symbol || '').toUpperCase();
              const bSym = (b.trading_symbol || b.symbol || '').toUpperCase();
              const aStarts = aSym.startsWith(uppercaseQuery) ? 0 : 1;
              const bStarts = bSym.startsWith(uppercaseQuery) ? 0 : 1;
              return aStarts - bStarts;
            });

            this.results = mergedResults;
          }
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
      }
    },
    searchLocal(uppercaseQuery) {
      const symbols = Object.values(this.allSymbols);

      let matched = symbols.filter(item => {
        const sym = (item.trading_symbol || item.symbol || '').toUpperCase();
        const name = (item.formatted_ins_name || item.name || '').toUpperCase();
        return sym.includes(uppercaseQuery) || name.includes(uppercaseQuery);
      });

      matched = this.applyResultFilter(matched);

      // Sort: exact prefix matches first
      matched.sort((a, b) => {
        const aSym = (a.trading_symbol || a.symbol || '').toUpperCase();
        const bSym = (b.trading_symbol || b.symbol || '').toUpperCase();
        const aStarts = aSym.startsWith(uppercaseQuery) ? 0 : 1;
        const bStarts = bSym.startsWith(uppercaseQuery) ? 0 : 1;
        return aStarts - bStarts;
      });

      return matched.slice(0, 150);
    },
    isFyersSymbol(item) {
      return item.type === 'Fyers' || item.exchange === 'Fyers' || !!item.fyToken;
    },
    isUpstoxSymbol(item) {
      return item.type === 'Upstox' || item.exchange === 'Upstox' || !!item.instrument_key;
    },
    isAliceBlueSymbol(item) {
      return item.type === 'AliceBlue' || item.exchange === 'AliceBlue' || !!item.token;
    },
    isIndianMarketSymbol(item) {
      return item.type === 'IndianMarket';
    },
    getIndianMarketRank(item) {
      const segment = (item.segment || item.exchange_segment || '').toUpperCase();
      const instrumentType = (item.instrument_type || item.instrumentType || '').toUpperCase();
      const symbol = (item.trading_symbol || item.symbol || '').toUpperCase();
      const isCommodity = segment.includes('MCX') || segment.includes('COM') || instrumentType.includes('COM');
      const isForex = segment.includes('CDS') || segment.includes('BCD') || segment.includes('CUR') || instrumentType.includes('CUR');
      const isOption = instrumentType.includes('OPT') || instrumentType === 'CE' || instrumentType === 'PE' || symbol.endsWith('CE') || symbol.endsWith('PE');
      const isFuture = instrumentType.includes('FUT') || symbol.includes('FUT');
      const isIndex = segment.includes('INDEX') || instrumentType.includes('INDEX') || segment.includes('IDX');
      const isStock = segment.includes('EQ') || instrumentType === 'EQ' || instrumentType === 'EQUITY';

      if (isStock || isIndex) return 0;
      if (!isCommodity && !isForex && isFuture) return 1;
      if (!isCommodity && !isForex && isOption) return 2;
      if (isCommodity && isFuture) return 3;
      if (isCommodity && isOption) return 4;
      if (isForex && isFuture) return 5;
      if (isForex && isOption) return 6;
      if (isFuture) return 7;
      if (isOption) return 8;
      return 9;
    },
    selectFirst() {
      if (this.filteredResults.length > 0) {
        this.selectItem(this.filteredResults[0]);
      }
    },
    selectItem(item) {
      this.$emit('select', item);
      this.close();
    },
    getItemSegmentKey(item) {
      // Crypto
      if (item.type === 'Crypto Spot') return 'crypto_spot';
      if (item.type === 'Crypto Futures' || item.contractType === 'PERPETUAL') return 'crypto_futures';

      // Fyers symbols — use symbol name and segment info
      if (this.isFyersSymbol(item)) {
        const sym = (item.symbol || item.trading_symbol || '').toUpperCase();
        const segment = (item.segment || item.exchange_segment || '').toUpperCase();

        // Indices
        if (sym.includes('-INDEX') || segment.includes('IDX')) return 'indices';
        // Options
        if (sym.includes('-OPT') || sym.includes('-CE') || sym.includes('-PE') || segment.includes('FO')) {
          // Distinguish futures from options within F&O segment
          if (sym.includes('-CE') || sym.includes('-PE') || sym.includes('-OPT')) return 'options';
          if (sym.includes('-FUT')) return 'futures';
          // If segment is FO but no clear suffix, check instrument type
          return 'futures';
        }
        if (sym.includes('-FUT')) return 'futures';
        // Commodity
        if (segment.includes('COM') || segment.includes('MCX') || segment.includes('CD')) return 'commodity';
        // Forex / Currency
        if (segment.includes('CDS') || segment.includes('BCD')) return 'forex';
        // Default for Fyers: stocks (NSE_CM, BSE_CM)
        return 'stocks';
      }

      // AliceBlue symbols — use symbol name and segment info
      if (this.isAliceBlueSymbol(item)) {
        const sym = (item.symbol || item.trading_symbol || '').toUpperCase();
        const segment = (item.segment || '').toUpperCase();

        // Indices
        if (segment === 'INDEX') return 'indices';
        // Options
        if (sym.includes('-OPT') || sym.includes('-CE') || sym.includes('-PE') || segment.includes('FO')) {
          // Distinguish futures from options within F&O segment
          if (sym.includes('-CE') || sym.includes('-PE') || sym.includes('-OPT')) return 'options';
          if (sym.includes('-FUT')) return 'futures';
          // If segment is FO but no clear suffix, check instrument type
          return 'futures';
        }
        if (sym.includes('-FUT')) return 'futures';
        // Commodity
        if (segment.includes('COM') || segment.includes('MCX') || segment.includes('CD')) return 'commodity';
        // Forex / Currency
        if (segment.includes('CDS') || segment.includes('BCD')) return 'forex';
        // Default for AliceBlue: stocks
        return 'stocks';
      }

      // Non-Fyers Indian symbols from static data
      if (this.isIndianMarketSymbol(item)) {
        const segment = (item.segment || '').toUpperCase();
        const instrumentType = (item.instrument_type || '').toUpperCase();
        if (segment.includes('INDEX') || instrumentType.includes('INDEX')) return 'indices';
        if (instrumentType.includes('OPT') || instrumentType === 'CE' || instrumentType === 'PE') return 'options';
        if (instrumentType.includes('FUT')) return 'futures';
        if (segment.includes('MCX') || segment.includes('COM')) return 'commodity';
        if (segment.includes('CDS') || segment.includes('BCD')) return 'forex';
        return 'stocks';
      }

      // Non-Fyers Indian symbols from static data
      if (item.exchange_segment === 'nse_idx' || (item.group_name && item.group_name === 'INDEX')) return 'indices';
      if (item.exchange_segment === 'nse_fo') return 'futures';
      if (item.exchange_segment === 'nse_cm' || item.exch === 'NSE' || item.exch === 'BSE') return 'stocks';

      return 'stocks';
    },
    getSegmentLabel(item) {
      const key = this.getItemSegmentKey(item);
      switch (key) {
        case 'crypto_spot': return 'spot crypto';
        case 'crypto_futures': return 'futures crypto';
        case 'indices': return 'indices';
        case 'futures': return 'futures';
        case 'options': return 'options';
        case 'commodity': return 'commodity';
        case 'forex': return 'forex';
        case 'stocks': {
          // Show exchange name for stocks
          if (this.isFyersSymbol(item)) {
            const sym = (item.symbol || '').toUpperCase();
            if (sym.startsWith('NSE:')) return 'NSE';
            if (sym.startsWith('BSE:')) return 'BSE';
            return item.exch || 'NSE';
          }
          if (this.isAliceBlueSymbol(item)) {
            return item.exchange || 'NSE';
          }
          if (this.isIndianMarketSymbol(item)) {
            return item.exchange || item.exch || 'NSE';
          }
          const seg = item.exchange_segment || '';
          if (seg === 'nse_cm') return 'NSE';
          if (item.exch) return item.exch;
          return 'stocks';
        }
        default: return '';
      }
    },
    getDisplaySymbol(item) {
      if (typeof this.displaySymbolFormatter === 'function') {
        const customSymbol = this.displaySymbolFormatter(item);
        if (customSymbol) return customSymbol;
      }
      let sym = item.trading_symbol || item.symbol || '';
      // Clean up suffixes for display
      sym = sym.replace(/-EQ$/, '');
      // Remove exchange prefix like NSE: or BSE:
      if (sym.includes(':')) sym = sym.split(':').pop();
      return sym;
    },
    getDisplayName(item) {
      if (typeof this.displayNameFormatter === 'function') {
        const customName = this.displayNameFormatter(item);
        if (customName) return customName;
      }
      return item.formatted_ins_name || item.name || item.baseAsset || '';
    },

    // ── Watchlist helpers ─────────────────────────────────────────────────────
    async refreshWatchlistKeys() {
      const list = await watchlistStore.load();
      this.watchlistKeys = new Set(list.map(e => e.key));
    },
    isInWatchlist(item) {
      const key = watchlistStore.keyFor(item);
      return key ? this.watchlistKeys.has(key) : false;
    },
    async toggleWatchlist(item) {
      const nowIn = await watchlistStore.toggleItem(item);
      // Rebuild the reactive key set so stars update immediately
      await this.refreshWatchlistKeys();
      // Force Vue to re-render the results list
      this.results = this.results.slice();
      this.$emit('watchlist-changed', { item, added: nowIn });
    },
  }
}
</script>

<style scoped>
.symbol-search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 5vh;
}

.symbol-search-modal {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.symbol-search-modal.dark {
  background: #1e1e2e;
  color: #e0e0e0;
}

.symbol-search-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px 12px;
}

.symbol-search-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.symbol-search-close {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #666;
  line-height: 1;
  padding: 0 4px;
}

.symbol-search-modal.dark .symbol-search-close {
  color: #aaa;
}

.symbol-search-input-wrapper {
  position: relative;
  padding: 0 24px 12px;
}

.symbol-search-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  font-size: 16px;
  outline: none;
  background: #fff;
  color: #333;
  box-sizing: border-box;
}

.symbol-search-modal.dark .symbol-search-input {
  background: #2a2a3e;
  border-color: #3a3a4e;
  color: #e0e0e0;
}

.symbol-search-input:focus {
  border-color: #2196F3;
}

.symbol-search-input {
  text-transform: uppercase;
}

.symbol-search-clear {
  position: absolute;
  right: 32px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 4px;
  display: none;
  align-items: center;
}

.symbol-search-filters {
  display: flex;
  gap: 8px;
  padding: 0 24px 14px;
  flex-wrap: nowrap;
}

.segment-btn {
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid #e0e0e0;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  color: #333;
  transition: all 0.15s;
  white-space: nowrap;
}

.symbol-search-modal.dark .segment-btn {
  background: #2a2a3e;
  border-color: #3a3a4e;
  color: #ccc;
}

.segment-btn.active {
  background: #1a1a1a;
  color: #fff;
  border-color: #1a1a1a;
}

.symbol-search-modal.dark .segment-btn.active {
  background: #4a9eff;
  border-color: #4a9eff;
  color: #fff;
}

.segment-btn:hover:not(.active) {
  background: #f5f5f5;
}

.symbol-search-modal.dark .segment-btn:hover:not(.active) {
  background: #333348;
}

.symbol-search-results {
  flex: 1;
  overflow-y: auto;
  border-top: 1px solid #eee;
}

.symbol-search-modal.dark .symbol-search-results {
  border-top-color: #3a3a4e;
}

.symbol-search-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  cursor: pointer;
  transition: background 0.1s;
}

.symbol-search-item:hover {
  background: #f7f7f7;
}

.symbol-search-modal.dark .symbol-search-item:hover {
  background: #2a2a3e;
}

.search-item-left {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  flex: 1;
}

.search-item-symbol {
  font-weight: 600;
  font-size: 14px;
  color: #1976D2;
  white-space: nowrap;
}

.symbol-search-modal.dark .search-item-symbol {
  color: #64b5f6;
}

.search-item-name {
  font-size: 13px;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.symbol-search-modal.dark .search-item-name {
  color: #aaa;
}

.search-item-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.search-item-segment {
  font-size: 12px;
  color: #888;
  white-space: nowrap;
}

/* ── Favourite / watchlist star button ─────────────────────────────────────── */
.search-item-fav-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #c0c4cc;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: color 0.15s, background 0.15s, transform 0.12s;
}

.search-item-fav-btn:hover {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
}

.search-item-fav-btn.is-fav {
  color: #f59e0b;
}

.search-item-fav-btn.is-fav:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.search-item-fav-btn:active {
  transform: scale(0.85);
}

.symbol-search-modal.dark .search-item-fav-btn {
  color: #4b5563;
}

.symbol-search-modal.dark .search-item-fav-btn:hover {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.15);
}

.symbol-search-modal.dark .search-item-fav-btn.is-fav {
  color: #f59e0b;
}

.symbol-search-modal.dark .search-item-fav-btn.is-fav:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.15);
}

.symbol-search-loading,
.symbol-search-empty {
  padding: 32px 24px;
  text-align: center;
  color: #888;
  font-size: 14px;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ddd;
  border-top-color: #2196F3;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
